import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { approveLoan } from '../../api/loans';
import { apiErrorMessage } from '../../lib/api';
import { useToast } from '../../components/ui/Toast';
import { Modal } from '../../components/ui/Modal';
import { Button } from '../../components/ui/Button';
import { Alert } from '../../components/ui/Alert';
import type { AssetLoan } from '../../types/api';

function itemName(loan: AssetLoan) {
  return loan.asset.orderLine?.customerCatalogItem?.name ?? 'this item';
}

export function ApproveLoanModal({
  loan,
  open,
  onClose,
}: {
  loan: AssetLoan;
  open: boolean;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: () => approveLoan(loan.id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['loans'] });
      queryClient.invalidateQueries({ queryKey: ['loan', loan.id] });
      queryClient.invalidateQueries({ queryKey: ['loans-queue'] });
      showToast(`Loan request for "${itemName(loan)}" approved.`);
      onClose();
    },
    onError: (err) => setError(apiErrorMessage(err, 'Could not approve the loan request.')),
  });

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Approve loan request"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button isLoading={mutation.isPending} onClick={() => mutation.mutate()}>
            Approve
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {error && <Alert tone="error">{error}</Alert>}
        <p className="text-sm text-slate-600">
          Approve {loan.requestedBy.firstName || loan.requestedBy.email}'s request to borrow{' '}
          <span className="font-medium text-slate-900">{itemName(loan)}</span> until{' '}
          {new Date(loan.dueDate).toLocaleDateString()}? It'll still need to be physically checked out to them
          afterwards.
        </p>
      </div>
    </Modal>
  );
}
